import React, { useState } from "react";
import { Drawer, DrawerContent } from "ui/Drawer";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "ui/Sheet";
import { ScrollArea } from "ui/ScrollArea";
import LoadingSkeleton from "ui/LoadingSkeleton";
import EmptyState from "ui/EmptyState";
import { useUser } from "util/UserContext";
import type { ChatConversation, ChatMessage, SharedEntityMeta } from "types/chat";
import { useChatThreadQuery } from "hooks/queries/useChatQueries";
import ChatMessageCard from "./ChatMessage";
import ChatComposer from "./ChatComposer";
import ContentViewerOverlay from "./ContentViewerOverlay";

interface ChatThreadProps {
  conversation: ChatConversation;
  threadRootMessage: ChatMessage | null;
  isOpen: boolean;
  onClose: () => void;
}

const ChatThread: React.FC<ChatThreadProps> = ({
  conversation,
  threadRootMessage,
  isOpen,
  onClose,
}) => {
  const { userIsMobile } = useUser();
  const { data, isLoading } = useChatThreadQuery(threadRootMessage?.public_id, isOpen && threadRootMessage !== null);
  const [selectedSharedEntity, setSelectedSharedEntity] = useState<SharedEntityMeta | null>(null);

  const content = (
    <div className="flex h-full flex-col">
      <ScrollArea className="flex-1">
        <div className="space-y-4 p-4">
          {isLoading ? (
            <LoadingSkeleton variant="comment-card" count={3} />
          ) : data && data.length > 0 ? (
            data.map((message) => (
              <ChatMessageCard
                key={message.public_id}
                conversation={conversation}
                message={message}
                onOpenSharedEntity={(sharedEntity) => setSelectedSharedEntity(sharedEntity)}
              />
            ))
          ) : (
            <EmptyState
              title="No replies yet"
              subtitle="Reply to start a focused side conversation."
            />
          )}
        </div>
      </ScrollArea>

      {threadRootMessage ? (
        <div className="border-t border-eoe-border-main dark:border-eoe-dark-border-main p-4">
          <ChatComposer
            conversationPublicId={conversation.public_id}
            threadRootMessagePublicId={threadRootMessage.public_id}
            replyToMessage={threadRootMessage}
            onSent={() => {
              // Query invalidation happens inside the mutation hooks.
            }}
            placeholder="Reply in thread"
          />
        </div>
      ) : null}
    </div>
  );

  if (userIsMobile) {
    return (
      <>
        <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
          <DrawerContent title="Thread" fullScreen className="!rounded-none !border-none">
            {content}
          </DrawerContent>
        </Drawer>
        <ContentViewerOverlay
          sharedEntity={selectedSharedEntity}
          isOpen={selectedSharedEntity !== null}
          onClose={() => setSelectedSharedEntity(null)}
        />
      </>
    );
  }

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="right" className="w-full sm:max-w-[420px] p-0">
          <SheetHeader className="border-b border-eoe-border-main p-4 dark:border-eoe-dark-border-main">
            <SheetTitle>Thread</SheetTitle>
          </SheetHeader>
          {content}
        </SheetContent>
      </Sheet>
      <ContentViewerOverlay
        sharedEntity={selectedSharedEntity}
        isOpen={selectedSharedEntity !== null}
        onClose={() => setSelectedSharedEntity(null)}
      />
    </>
  );
};

export default ChatThread;
